import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PROJECT_TYPES } from '@jobos/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { Button } from '@/components/ui/button';
import { useCreateBreakdownTemplate } from '@/hooks/useBreakdownTemplates';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PROJECT_TYPE_OPTIONS = PROJECT_TYPES.map((t) => ({ value: t, label: t }));

export function NewTemplateDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const createM = useCreateBreakdownTemplate();

  const [name, setName] = useState('');
  const [projectType, setProjectType] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setProjectType('');
    setError(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    if (!projectType) {
      setError('Project type is required');
      return;
    }
    try {
      const created = await createM.mutateAsync({ projectType, name: trimmed });
      reset();
      onOpenChange(false);
      navigate(`/settings/job-type-templates/${created.id}/edit`);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New template</DialogTitle>
          <DialogDescription>
            Create a blank breakdown template, then edit its contents.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              placeholder="Template name"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project type</label>
            <Combobox
              value={projectType}
              onValueChange={(v) => {
                setProjectType(v);
                if (error) setError(null);
              }}
              options={PROJECT_TYPE_OPTIONS}
              placeholder="Select project type"
              searchPlaceholder="Search project types..."
              emptyText="No project type found."
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || !projectType || createM.isPending}
          >
            {createM.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
